import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { CheckCircle, Loader2, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const { i18n } = useTranslation();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get("session_id");
  const isFrench = i18n.language === 'fr';

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= 10) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.data.payment_status === "paid") {
        setStatus("success");
        // Refresh user data
        window.location.reload();
      } else if (response.data.status === "expired") {
        setStatus("expired");
      } else {
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error("Error checking payment:", error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-16 h-16 text-[#2F4F4F] animate-spin mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-semibold text-[#2D2D2D] mb-4">
              {isFrench ? "Vérification du paiement..." : "Checking payment..."}
            </h1>
            <p className="text-[#5C5C5C]">
              {isFrench ? "Veuillez patienter quelques instants" : "Please wait a moment"}
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#A8C3A0]/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-[#A8C3A0]" />
            </div>
            <h1 className="font-heading text-3xl font-semibold text-[#2D2D2D] mb-4">
              {isFrench ? "Paiement réussi !" : "Payment successful!"}
            </h1>
            <p className="text-[#5C5C5C] mb-8">
              {isFrench 
                ? "Félicitations ! Vous avez maintenant accès à toutes les fonctionnalités Premium."
                : "Congratulations! You now have access to all Premium features."}
            </p>
            <div className="space-y-4">
              <Link to="/diagnostic">
                <Button className="w-full rounded-full bg-[#2F4F4F] hover:bg-[#2F4F4F]/90 text-white h-12">
                  {isFrench ? "Faire un diagnostic" : "Take a diagnostic"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/history">
                <Button variant="outline" className="w-full rounded-full border-gray-200 h-12">
                  {isFrench ? "Voir mon historique" : "View my history"}
                </Button>
              </Link>
            </div>
          </>
        )}

        {(status === "timeout" || status === "expired") && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#E2725B]/20 flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-12 h-12 text-[#E2725B]" />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-[#2D2D2D] mb-4">
              {isFrench ? "Statut inconnu" : "Unknown status"}
            </h1>
            <p className="text-[#5C5C5C] mb-8">
              {isFrench 
                ? "Nous n'avons pas pu confirmer votre paiement. Si vous avez été débité, contactez-nous."
                : "We couldn't confirm your payment. If you were charged, please contact us."}
            </p>
            <Link to="/">
              <Button className="rounded-full bg-[#2F4F4F] text-white">
                {isFrench ? "Retour à l'accueil" : "Back to home"}
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
